import React, { useState, useRef, useEffect } from 'react';
// Remove @chatscope imports
// import {
//     MainContainer,
//     ChatContainer,
//     MessageList,
//     Message,
//     MessageInput,
//     ConversationHeader,
//     Avatar,
// } from "@chatscope/chat-ui-kit-react";
// Remove @chatscope styles import
// import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import './ChatBot.css';

// Assuming FontAwesome is available for icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

const ChatBot = ({ isOpen, setIsOpen, darkMode }) => {
    // Keep messages state for demonstration, but you'll build your own message rendering
    const [messages, setMessages] = useState([
        {
            text: "Hello! I'm your Elyspr AI Assistant. How can I help you today? ",
            sender: "bot",
        },
        // Remove the second initial message
        // {
        //     text: "This is a custom chatbot interface!",
        //     sender: "bot",
        // }
    ]);
    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    // Add refs for auto-scrolling and input focus
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Check for system color scheme preference
    useEffect(() => {
        // Function to check dark mode
        const checkDarkMode = () => {
            // Check if user has explicitly set dark mode
            const isDarkModeSet = window.matchMedia('(prefers-color-scheme: dark)').matches;
            console.log('Dark mode detected:', isDarkModeSet);
        };

        // Initial check
        checkDarkMode();

        // Listen for changes
        const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            console.log('Color scheme changed:', e.matches ? 'dark' : 'light');
        };

        darkModeMediaQuery.addEventListener('change', handleChange);

        return () => darkModeMediaQuery.removeEventListener('change', handleChange);
    }, []);

    // Auto-scroll to bottom when messages change
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Focus input when chat opens
    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    // Scroll to bottom when messages update
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Keep input focused after loading state changes
    useEffect(() => {
        if (!isLoading) {
            inputRef.current?.focus();
        }
    }, [isLoading]);

    const handleSendMessage = async () => {
        if (inputText.trim() && !isLoading) {
            const newMessage = {
                text: inputText,
                sender: "user",
            };
            setMessages(prevMessages => [...prevMessages, newMessage]);
            setInputText('');
            setIsLoading(true);

            // Determine the correct API endpoint based on environment
            const apiEndpoint = process.env.NODE_ENV === 'development' 
                ? 'http://127.0.0.1:5000/api/chat' 
                : '/api/chat';

            try {
                // Send message to the backend API
                const response = await fetch(apiEndpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ message: newMessage.text }),
                });

                if (!response.ok) {
                    // Handle HTTP errors
                    const errorData = await response.json();
                    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                const botResponse = {
                    text: data.response, // Assuming the backend sends the response in a 'response' field
                    sender: "bot",
                };
                setMessages(prevMessages => [...prevMessages, botResponse]);

            } catch (error) {
                console.error('Error sending message:', error);
                const errorMessage = {
                    text: "Sorry, something went wrong. Please try again.",
                    sender: "bot",
                };
                 setMessages(prevMessages => [...prevMessages, errorMessage]);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSendMessage();
        }
    };

    return (
        <div className={`chatbot-container ${isOpen ? 'open' : ''}`}>
            {/* The toggle button is now handled in App.js */}
            {isOpen && (
                <div className="chatbot-window">
                    <div className="chat-header">
                        <div className="logo-container">
                            <img src={darkMode ? "/logo3.avif" : "/logo-1.avif"} alt="Logo" />
                            <span className="title">AI Assistant</span>
                        </div>
                        <button className="close-button" onClick={() => setIsOpen(false)} aria-label="Close chat">
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    <div className="message-area">
                        {messages.map((msg, index) => (
                            <div key={index} className={`message ${msg.sender}`}>
                                <p style={msg.sender === 'bot' ? { whiteSpace: 'pre-line' } : {}}>{msg.text}</p>
                            </div>
                        ))}
                         {/* Add a loading indicator here if needed when waiting for bot response */}
                         {isLoading && (
                            <div className="message bot loading">
                                <p><span className="typing-indicator">Elyspr is typing<span className="dot">.</span><span className="dot">.</span><span className="dot">.</span></span></p>
                            </div>
                         )}
                        <div ref={messagesEndRef} /> {/* Add this div for auto-scrolling */}
                    </div>
                    <div className="input-area">
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Type a message..."
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading} // Disable input while loading
                            autoFocus
                        />
                        <button 
                            onClick={handleSendMessage} 
                            disabled={isLoading}
                            onMouseDown={(e) => e.preventDefault()} // Prevent focus loss on button click
                        >
                            Send
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatBot; 